import { useEffect, useCallback, useRef, useState } from "react";

type Props = {
  enabled: boolean;
  windowId?: string;
  onNavigate?: (itemId: number, selectedItems: Set<number>) => void;
  debounceMs?: number;
};

export const useKeyboardNavigation = ({
  enabled,
  windowId,
  onNavigate,
  debounceMs = 250,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [focusedItemId, setFocusedItemId] = useState<number | null>(null);

  const multiSelectStackRef = useRef<number[]>([]);
  const baseSelectionRef = useRef<Set<number>>(new Set());

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastEmittedKeyRef = useRef<string>("");

  const getVisibleItemIds = useCallback(() => {
    if (!containerRef.current) return [];

    const selector = windowId ? `[data-item-id][data-window-id="${windowId}"]` : "[data-item-id]";
    const elements = containerRef.current.querySelectorAll(selector);

    return Array.from(elements)
      .map((el) => parseInt(el.getAttribute("data-item-id") || "0"))
      .filter((id) => id > 0);
  }, [windowId]);

  const focusItemById = useCallback(
    (itemId: number) => {
      const selector = windowId
        ? `[data-item-id="${itemId}"][data-window-id="${windowId}"]`
        : `[data-item-id="${itemId}"]`;
      const element = (containerRef.current ?? document).querySelector(selector) as HTMLElement;

      if (element) {
        element.focus();
        element.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    },
    [windowId]
  );

  const emitNavigate = useCallback(
    (itemId: number, selection: Set<number>) => {
      if (!onNavigate) return;

      const key = `${itemId}:${[...selection].sort().join(",")}`;
      if (key === lastEmittedKeyRef.current) return;

      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

      debounceTimerRef.current = setTimeout(() => {
        lastEmittedKeyRef.current = key;
        onNavigate(itemId, selection);
      }, debounceMs);
    },
    [onNavigate, debounceMs]
  );

  const navigateToItem = useCallback(
    (direction: "next" | "prev", event?: KeyboardEvent) => {
      const visibleIds = getVisibleItemIds();
      if (visibleIds.length === 0) return;

      const currentIndex = focusedItemId ? visibleIds.indexOf(focusedItemId) : -1;

      let targetIndex: number;
      if (direction === "next") {
        targetIndex = currentIndex < visibleIds.length - 1 ? currentIndex + 1 : 0;
      } else {
        targetIndex = currentIndex > 0 ? currentIndex - 1 : visibleIds.length - 1;
      }

      const targetId = visibleIds[targetIndex];
      if (targetId === undefined) return;

      setFocusedItemId(targetId);
      focusItemById(targetId);

      if (onNavigate) {
        const isMulti = event ? event.ctrlKey || event.metaKey || event.shiftKey : false;

        if (isMulti) {
          const stack = multiSelectStackRef.current;

          if (stack.length === 0) {
            baseSelectionRef.current = focusedItemId ? new Set([focusedItemId]) : new Set<number>();
            stack.push(...(focusedItemId ? [focusedItemId] : []));
          }

          const secondToLast = stack.length >= 2 ? stack[stack.length - 2] : undefined;

          if (secondToLast === targetId) {
            stack.pop();
          } else {
            stack.push(targetId);
          }

          const selection = new Set(baseSelectionRef.current);
          for (const id of stack) selection.add(id);

          emitNavigate(targetId, selection);
        } else {
          multiSelectStackRef.current = [];
          baseSelectionRef.current = new Set();
          emitNavigate(targetId, new Set([targetId]));
        }
      }
    },
    [focusedItemId, getVisibleItemIds, focusItemById, onNavigate, emitNavigate]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          navigateToItem("next", event);
          break;

        case "ArrowUp":
          event.preventDefault();
          navigateToItem("prev", event);
          break;
      }
    },
    [enabled, navigateToItem]
  );

  useEffect(() => {
    const handleKeyUp = (event: KeyboardEvent) => {
      if (!event.ctrlKey && !event.metaKey && !event.shiftKey) {
        multiSelectStackRef.current = [];
        baseSelectionRef.current = new Set();
      }
    };

    window.addEventListener("keyup", handleKeyUp);
    return () => window.removeEventListener("keyup", handleKeyUp);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return;

    container.addEventListener("keydown", handleKeyDown);

    return () => {
      container.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown, enabled]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  return { containerRef, setFocusedItem: setFocusedItemId, getVisibleItemIds };
};
